/**
 * Authentication Initialization Hook
 *
 * This hook handles the initialization of authentication state on app load.
 * It validates stored tokens, refreshes them if needed, and ensures proper
 * authentication state restoration across page refreshes.
 */

import { useAuthActions, useCurrentUser } from "@/hooks/api/useAuthActions"
import { useAuthStore } from "@/stores/auth"
import type { User } from "@/types/auth"
import { isTokenExpired, isValidJWT } from "@/utils/jwt"
import { useEffect, useRef, useState } from "react"

export function useAuthInitialization() {
  const {
    accessToken,
    refreshToken: storedRefreshToken,
    isAuthenticated,
    user,
    clearAuth,
    setUser,
  } = useAuthStore()
  const { refreshToken: refreshMutation } = useAuthActions({
    skipNavigation: true,
  })
  const refreshAccessToken = refreshMutation.mutateAsync
  const isInitialized = useRef(false)
  const [isInitializing, setIsInitializing] = useState(false)

  // Use the current user query to fetch user data if needed
  const { data: userData } = useCurrentUser()

  useEffect(() => {
    const initializeAuth = async () => {
      // Only run once
      if (isInitialized.current) return
      isInitialized.current = true

      // If no access token is stored, we're not authenticated
      if (!accessToken) {
        return
      }

      // Start initialization
      setIsInitializing(true)

      try {
        // Validate token format
        if (!isValidJWT(accessToken)) {
          clearAuth()
          return
        }

        // Check if token is expired
        if (isTokenExpired(accessToken)) {
          if (!storedRefreshToken) {
            clearAuth()
            return
          }

          try {
            // Attempt to refresh the token using stored refresh token
            await refreshAccessToken(storedRefreshToken)
          } catch (error) {
            console.error("Token refresh failed:", error)
            clearAuth()
            return
          }
        }
      } finally {
        setIsInitializing(false)
      }
    }

    initializeAuth()
  }, [accessToken, storedRefreshToken, clearAuth, refreshAccessToken])

  // Update user data in store when it's fetched
  useEffect(() => {
    if (
      userData &&
      typeof userData === "object" &&
      "id" in userData &&
      (!user || user.id !== userData.id)
    ) {
      setUser(userData as User)
    }
  }, [userData, user, setUser])

  return {
    isInitializing,
    isAuthenticated,
  }
}
